// src/components/buyerPersona/BuyerPersonaCard.jsx
import React, { useState, useEffect } from 'react';
import { FaUser, FaPlus, FaSpinner, FaMapMarkerAlt, FaGraduationCap, FaBriefcase, FaHeart, FaExclamationTriangle, FaLightbulb, FaQuoteLeft, FaShoppingCart, FaClock, FaEuroSign, FaUsers, FaMobile, FaGlobe, FaChartLine, FaBullseye, FaHandshake } from 'react-icons/fa';
import { useBuyerPersonaQuiz } from '../../hooks/useBuyerPersonaQuiz';
import BuyerPersonaModal from './BuyerPersonaModal';

const BuyerPersonaCard = () => {
    const [showModal, setShowModal] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [shouldReload, setShouldReload] = useState(false);
    const [imageError, setImageError] = useState(false);
    const { result, loading, getBuyerPersona } = useBuyerPersonaQuiz();

    // Cargar buyer persona al montar el componente (solo una vez)
    useEffect(() => {
        if (!hasLoaded || shouldReload) {
            setHasLoaded(true);
            setShouldReload(false);
            getBuyerPersona();
        }
    }, [hasLoaded, shouldReload, getBuyerPersona]);

    const handleCreatePersona = () => {
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setTimeout(() => {
            setShouldReload(true);
        }, 1000);
    };

    // Función para construir la URL completa de la imagen
    const getImageUrl = (imageUrl) => {
        if (!imageUrl) return null;

        try {
            // Si ya es una URL completa, usarla tal como está
            if (imageUrl.startsWith('http')) {
                return imageUrl;
            }

            // Construir URL basada en el ambiente
            const baseUrl = process.env.REACT_APP_API_URL ||
                (process.env.NODE_ENV === 'development'
                    ? 'http://localhost:3000'
                    : 'https://api.petitprime.com');

            // Asegurarnos de que la URL tenga el formato correcto
            const cleanImageUrl = imageUrl.startsWith('/') ? imageUrl : `/api/buyer-persona/image/${imageUrl}`;
            return `${baseUrl}${cleanImageUrl}`;
        } catch (error) {
            return null;
        }
    };

    const handleImageError = (e) => {
        setImageError(true);
        e.target.style.display = 'none';
        const fallbackAvatar = e.target.nextSibling;
        if (fallbackAvatar) {
            fallbackAvatar.style.display = 'flex';
        }
    };

    const handleImageLoad = () => {
        setImageError(false);
    };

    if (loading) {
        return (
            <div className="buyer-persona-card">
                <div className="flex items-center justify-center h-64">
                    <FaSpinner className="animate-spin text-blue-500 text-2xl mr-3" />
                    <span className="text-gray-600">Cargando buyer persona...</span>
                </div>
            </div>
        );
    }

    // Si no hay buyer persona, mostrar botón para crear
    if (!result) {
        return (
            <div className="buyer-persona-card">
                <div className="text-center py-12">
                    <div className="text-gray-400 text-6xl mb-6">
                        <FaUser className="mx-auto" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">
                        Buyer Persona
                    </h3>
                    <p className="text-gray-600 mb-8 max-w-md mx-auto">
                        Crea tu buyer persona para diseñar mejores campañas de marketing y entender mejor a tus clientes
                    </p>
                    <button
                        onClick={handleCreatePersona}
                        className="bg-blue-500 text-white px-8 py-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center mx-auto text-lg font-semibold"
                    >
                        <FaPlus className="mr-2" />
                        Crear Buyer Persona
                    </button>
                </div>

                {showModal && (
                    <BuyerPersonaModal
                        isOpen={showModal}
                        onClose={handleModalClose}
                        mode="create"
                    />
                )}
            </div>
        );
    }



    // Función para obtener icono según el canal de comunicación
    const getChannelIcon = (channel) => {
        const channelIcons = {
            'Instagram': FaMobile,
            'Google': FaGlobe,
            'TikTok': FaMobile,
            'Boca a boca': FaUsers,
            'Facebook': FaUsers,
            'WhatsApp': FaMobile,
            'Email': FaGlobe,
            'LinkedIn': FaBriefcase
        };
        return channelIcons[channel] || FaGlobe;
    };

    // Función para obtener el nivel de digitalización como badge
    const getDigitalizationBadge = (nivel) => {
        const levels = {
            'bajo': { color: 'bg-red-100 text-red-800', text: 'Bajo' },
            'intermedio': { color: 'bg-yellow-100 text-yellow-800', text: 'Intermedio' },
            'alto': { color: 'bg-green-100 text-green-800', text: 'Alto' }
        };
        return levels[nivel?.toLowerCase()] || levels.intermedio;
    };

    return (
        <div className="buyer-persona-card">
            <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Buyer Persona</h3>
            </div>

            <div className="buyer-persona-profile">
                {/* Sección superior - Información básica */}
                <div className="w-full mb-6">
                    <div className="flex items-start space-x-6">
                        {/* Avatar y nombre */}
                        <div className="flex-shrink-0">
                            {result?.imageUrl ? (
                                <div className="relative">
                                    <img
                                        src={getImageUrl(result.imageUrl)}
                                        alt={result.nombre}
                                        className="w-20 h-20 rounded-full object-cover border-4 border-blue-200 shadow-lg"
                                        onError={handleImageError}
                                        onLoad={handleImageLoad}
                                    />
                                    <div
                                        className={`w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 
                                            flex items-center justify-center text-white text-2xl font-bold 
                                            border-4 border-blue-200 shadow-lg absolute top-0 left-0
                                            ${!imageError ? 'hidden' : ''}`}
                                    >
                                        {result.nombre.charAt(0).toUpperCase()}
                                    </div>
                                </div>
                            ) : (
                                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 
                                    flex items-center justify-center text-white text-2xl font-bold 
                                    border-4 border-blue-200 shadow-lg">
                                    {result.nombre.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>

                        {/* Información personal */}
                        <div className="flex-1">
                            <h4 className="text-xl font-bold text-gray-800 mb-2">{result.nombre}</h4>
                            <p className="text-gray-600 text-sm mb-3">{result.edadMedia} años • {result.genero}</p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex items-center">
                                    <FaMapMarkerAlt className="text-gray-400 mr-2 w-4" />
                                    <span className="text-gray-700 text-sm">{result.ubicacion}</span>
                                </div>
                                <div className="flex items-center">
                                    <FaBriefcase className="text-gray-400 mr-2 w-4" />
                                    <span className="text-gray-700 text-sm">{result.ocupacion}</span>
                                </div>
                                <div className="flex items-center">
                                    <FaGraduationCap className="text-gray-400 mr-2 w-4" />
                                    <span className="text-gray-700 text-sm">{result.nivelAdquisitivo}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sección de métricas de negocio */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold">Tipo Cliente</p>
                                <p className="text-sm font-bold text-gray-800">{result.tipoCliente}</p>
                            </div>
                            <FaUsers className="text-blue-500 text-lg" />
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold">Frecuencia</p>
                                <p className="text-sm font-bold text-gray-800">{result.frecuenciaCompra}</p>
                            </div>
                            <FaClock className="text-green-500 text-lg" />
                        </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold">Presupuesto</p>
                                <p className="text-sm font-bold text-gray-800">{result.presupuesto}</p>
                            </div>
                            <FaEuroSign className="text-purple-500 text-lg" />
                        </div>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold">Digitalización</p>
                                <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getDigitalizationBadge(result.nivelDigitalizacion).color}`}>
                                    {getDigitalizationBadge(result.nivelDigitalizacion).text}
                                </span>
                            </div>
                            <FaMobile className="text-orange-500 text-lg" />
                        </div>
                    </div>
                </div>

                {/* Sección inferior - Descripción, Objetivos/Desafíos/Necesidades y Citas */}
                <div className="space-y-6">
                    {/* Descripción */}
                    <div className="buyer-persona-section">
                        <h4 className="flex items-center">
                            <FaUser className="mr-2 text-blue-500" />
                            {result.nombre}
                        </h4>
                        <p className="text-gray-700 text-sm leading-relaxed">
                            {result.descripcion || `${result.nombre} es un ${result.tipoCliente.toLowerCase()} que realiza compras ${result.frecuenciaCompra.toLowerCase()}. Con un presupuesto de ${result.presupuesto}, busca productos que se alineen con sus necesidades y valores.`}
                        </p>
                    </div>

                    {/* Objetivos, Desafíos y Necesidades */}
                    <div className="buyer-persona-section">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <h4 className="flex items-center text-green-600">
                                    <FaHeart className="mr-2" />
                                    OBJETIVOS
                                </h4>
                                <ul className="text-sm text-gray-700 space-y-2 mt-3">
                                    {result.motivaciones?.map((motivacion, index) => (
                                        <li key={index} className="flex items-start">
                                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            {motivacion}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div>
                                <h4 className="flex items-center text-red-600">
                                    <FaExclamationTriangle className="mr-2" />
                                    DESAFÍOS
                                </h4>
                                <ul className="text-sm text-gray-700 space-y-2 mt-3">
                                    {result.problemas?.map((problema, index) => (
                                        <li key={index} className="flex items-start">
                                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            {problema}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div>
                                <h4 className="flex items-center text-blue-600">
                                    <FaLightbulb className="mr-2" />
                                    NECESIDADES
                                </h4>
                                <ul className="text-sm text-gray-700 space-y-2 mt-3">
                                    {result.valores?.map((valor, index) => (
                                        <li key={index} className="flex items-start">
                                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            {valor}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Canales de comunicación */}
                    {result.canalesComunicacion && result.canalesComunicacion.length > 0 && (
                        <div className="buyer-persona-section">
                            <h4 className="flex items-center">
                                <FaHandshake className="mr-2 text-indigo-500" />
                                CANALES DE COMUNICACIÓN
                            </h4>
                            <div className="flex flex-wrap gap-2 mt-3">
                                {result.canalesComunicacion.map((canal, index) => {
                                    const IconComponent = getChannelIcon(canal);
                                    return (
                                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                            <IconComponent className="mr-1 w-3 h-3" />
                                            {canal}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Estilo de comunicación */}
                    {result.estiloComunicacion && (
                        <div className="buyer-persona-section">
                            <h4 className="flex items-center">
                                <FaBullseye className="mr-2 text-pink-500" />
                                ESTILO DE COMUNICACIÓN
                            </h4>
                            <p className="text-sm text-gray-700 mt-2">
                                {result.estiloComunicacion}
                            </p>
                        </div>
                    )}


                </div>
            </div>

            {showModal && (
                <BuyerPersonaModal
                    isOpen={showModal}
                    onClose={handleModalClose}
                    mode="edit"
                    existingPersona={result}
                />
            )}
        </div>
    );
};

export default BuyerPersonaCard; 